import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { httpclient as Http } from 'typescript-http-client';
import { DiagnosticResult, DiagnosticStatuses } from '../../app/dto/diagnosticResultDto';
import DiagnosticTranslations from '../../app/dto/translations/diagnosticTranslations';
import { AppThunk } from '../../app/store';

export interface DiagnosticsState {
  isRunning: boolean;
  isLoading: boolean;
  result: DiagnosticResult | null;
  error: string | null;
}

const initialState: DiagnosticsState = {
  isRunning: false,
  isLoading: false,
  result: null,
  error: null
};

const POLL_INTERVAL_MS = 2000;
const POLL_DEADLINE_MS = 25000;

export const diagnosticSlice = createSlice({
  name: 'diagnostic',
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
      if (action.payload) {
        state.error = null;
      }
    },
    setRunning: (state) => {
      state.isRunning = true;
      state.error = null;
    },
    setResult: (state, action: PayloadAction<DiagnosticResult | null>) => {
      state.result = action.payload;
      state.isRunning = action.payload?.status === DiagnosticStatuses.Pending;
      state.error = null;
    },
    setError: (state, action: PayloadAction<string>) => {
      state.isRunning = false;
      state.isLoading = false;
      state.error = action.payload;
    }
  }
});

export const { setLoading, setRunning, setResult, setError } = diagnosticSlice.actions;

async function fetchLast(apiClient: Http.HttpClient): Promise<DiagnosticResult | null> {
  const request = new Http.Request('diagnostics/last', { method: 'GET' });
  const response = await apiClient.execute<DiagnosticResult | ''>(request);
  return response && typeof response === 'object' ? response : null;
}

async function fetchDiagnostic(apiClient: Http.HttpClient, id: string): Promise<DiagnosticResult | null> {
  const request = new Http.Request(`diagnostics/${id}`, { method: 'GET' });
  const response = await apiClient.execute<DiagnosticResult | ''>(request);
  return response && typeof response === 'object' ? response : null;
}

export function loadLastDiagnosticAsync(apiClient: Http.HttpClient, translations: DiagnosticTranslations): AppThunk {
  return async (dispatch) => {
    dispatch(setLoading(true));
    try {
      const last = await fetchLast(apiClient);
      dispatch(setResult(last));
    } catch (e) {
      const message = e instanceof Error ? e.message : translations.LoadFailed;
      dispatch(setError(message));
    } finally {
      dispatch(setLoading(false));
    }
  };
}

async function pollUntilResolved(
  apiClient: Http.HttpClient,
  dispatch: (action: ReturnType<typeof setResult>) => void,
  id: string,
  signal?: AbortSignal
): Promise<boolean> {
  const deadline = Date.now() + POLL_DEADLINE_MS;

  while (!signal?.aborted && Date.now() < deadline) {
    await new Promise<void>((resolve) => window.setTimeout(resolve, POLL_INTERVAL_MS));
    if (signal?.aborted) return true;

    try {
      const latest = await fetchDiagnostic(apiClient, id);
      if (signal?.aborted || !latest) continue;
      dispatch(setResult(latest));
      if (latest.status !== DiagnosticStatuses.Pending) return true;
    } catch {
      // Transient failure: keep polling until the deadline.
    }
  }

  return signal?.aborted ?? false;
}

export function runDiagnosticsAsync(apiClient: Http.HttpClient, translations: DiagnosticTranslations, signal?: AbortSignal): AppThunk {
  return async (dispatch) => {
    dispatch(setRunning());

    try {
      const initial = await apiClient.execute<DiagnosticResult>(new Http.Request('diagnostics', { method: 'POST' }));
      if (signal?.aborted) return;
      dispatch(setResult(initial));
      if (initial.status !== DiagnosticStatuses.Pending) return;

      const resolved = await pollUntilResolved(apiClient, dispatch, initial.id, signal);
      if (!resolved) {
        dispatch(setError(translations.PollTimeout));
      }
    } catch (e) {
      if (signal?.aborted) return;
      const message = e instanceof Error ? e.message : translations.RequestFailed;
      dispatch(setError(message));
    }
  };
}

export default diagnosticSlice.reducer;
